import { sanitize } from "lettersanitizer";
import { EmailMessage } from "protogen/conversation_pb";
import { Box, Button, IconButton } from "@mui/material";
import { List } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { Attachment } from "protogen/common_pb";
import { useState } from "react";
import AttachmentDialog, {
  isSupportedAttachment,
} from "../common/AttachmentDialog";
import { FileDown } from "lucide-react";
import { downloadAttachment } from "../editor/AttachmentList";
import { humanFileSize } from "../../common/utils";
import IcsAttachmentButton from "./IcsAttachmentButton";

const DELIVERY_FAILURE_SUBJECT = "Delivery Status Notification (Failure)";

const containsBlockquote = (html: string): boolean => {
  const openTag = /<blockquote\b[^>]*>/; // checks for <blockquote> with any attributes
  const closeTag = /<\/blockquote>/; // checks for </blockquote>
  return openTag.test(html) && closeTag.test(html);
};

const Attachments = ({
  attachments,
  email,
}: {
  attachments: Attachment[];
  email: EmailMessage;
}) => {
  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);
  const onAttachmentClick = async (attachment: Attachment) => {
    if (isSupportedAttachment(attachment)) {
      // Handle document...
      setOpenAttachment(attachment);
    } else {
      await downloadAttachment(attachment);
    }
  };

  return (
    <List>
      {attachments.map((attachment, i) => (
        <ListItem key={i} sx={{ backgroundColor: "#F3F6F9", marginTop: "8px" }}>
          <IconButton
            sx={{
              marginRight: "8px",
            }}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await downloadAttachment(attachment);
            }}
          >
            <FileDown height="24px" width="24px" />
          </IconButton>
          <ListItemText
            primary={
              <div
                style={{ fontSize: "14px", cursor: "pointer" }}
                onClick={async () => await onAttachmentClick(attachment)}
              >
                <span style={{ fontWeight: 600 }}>{attachment.filename}</span>
                <span style={{ marginLeft: "5px", fontStyle: "italic" }}>
                  ({humanFileSize(attachment.fileSize)})
                </span>
              </div>
            }
          />
          <IcsAttachmentButton
            email={email}
            attachment={attachment}
            attachmentIndex={i}
          />
          {/*<ListItemSecondaryAction>*/}
          {/*  <IconButton edge="end" aria-label="delete" onClick={() => onDelete(attachment.filename)}>*/}
          {/*    <CloseIcon />*/}
          {/*  </IconButton>*/}
          {/*</ListItemSecondaryAction>*/}
        </ListItem>
      ))}
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
    </List>
  );
};

const containsOutlookSignature = (html: string): boolean => {
  // Alternatively, MS outlook seems to just use <hr> to separate past emails
  return /ms-outlook-mobile-signature/.test(html);
};

const stripExcessiveBidiTags = (html: string): string => {
  // Remove excessive nested <bdi> and unicode-bidi spans that break rendering
  // Pattern: <span style="unicode-bidi: embed;" dir="auto"><bdi>
  let cleaned = html;
  let prevLength = 0;

  // Iteratively strip nested patterns until no more changes occur
  while (cleaned.length !== prevLength) {
    prevLength = cleaned.length;
    // Remove opening bidi span+bdi wrapper
    cleaned = cleaned.replace(
      /<span\s+style="unicode-bidi:\s*embed;"\s+dir="auto"><bdi>/g,
      "",
    );
    // Remove closing bdi+span wrapper
    cleaned = cleaned.replace(/<\/bdi><\/span>/g, "");
  }

  return cleaned;
};

const replaceCheckboxesWithIcons = (html: string): string => {
  // Replace checked checkboxes with styled checked icon
  // Match both <input type="checkbox" checked> and <input checked="" type="checkbox"/>
  let result = html.replace(
    /<input[^>]*\bchecked\b[^>]*\btype\s*=\s*["']checkbox["'][^>]*\/?>/gi,
    '<span style="display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; border: 2px solid #2196F3; background-color: #2196F3; border-radius: 3px; vertical-align: middle; color: white; font-size: 12px; font-weight: bold; line-height: 1;">✓</span>',
  );
  result = result.replace(
    /<input[^>]*\btype\s*=\s*["']checkbox["'][^>]*\bchecked\b[^>]*\/?>/gi,
    '<span style="display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; border: 2px solid #2196F3; background-color: #2196F3; border-radius: 3px; vertical-align: middle; color: white; font-size: 12px; font-weight: bold; line-height: 1;">✓</span>',
  );
  // Replace unchecked checkboxes with styled unchecked icon
  result = result.replace(
    /<input[^>]*\btype\s*=\s*["']checkbox["'][^>]*\/?>/gi,
    '<span style="display: inline-block; width: 14px; height: 14px; border: 2px solid #666; background-color: white; border-radius: 3px; vertical-align: middle;"></span>',
  );
  return result;
};

const containsHtmlMarkup = (content: string): boolean => {
  // Match a doctype or a syntactically valid opening/closing tag. Requiring
  // whitespace before attributes keeps plain-text addresses such as
  // <person@example.com> from being mistaken for HTML.
  return /<!doctype\s+html\b|<\/?[a-z][a-z0-9-]*(?:\s+[^<>]*?)?\s*\/?>/i.test(
    content,
  );
};

const sanitizeEmailContent = (html: string, text: string): string => {
  // Some providers put the plain-text body in both fields. Passing that value
  // to lettersanitizer as HTML is unsafe: angle-bracketed email addresses are
  // parsed as nested custom elements, and the sanitizer can expand that tree
  // until the browser renderer crashes. An empty HTML value makes the library
  // use its text-escaping path instead. Other providers put an actual HTML-only
  // body in both fields, so preserve equal values that contain HTML markup.
  const duplicatedPlainText = html === text && !containsHtmlMarkup(html);
  const htmlToSanitize = duplicatedPlainText ? "" : html;

  // Some emails contain excessive <bdi> and unicode-bidi spans that break rendering.
  // Let's strip them out. Note: This might just be an issue with one email, but simple fix.
  const cleanedHtml = stripExcessiveBidiTags(htmlToSanitize || "");

  // Replace checkbox inputs with static icons
  const htmlWithoutCheckboxes = replaceCheckboxesWithIcons(cleanedHtml);

  return sanitize(htmlWithoutCheckboxes, text, {});
};

export default ({ email }: { email: EmailMessage }) => {
  const [showOriginal, setShowOriginal] = useState(
    !(containsBlockquote(email.html) || containsOutlookSignature(email.html)),
  );
  // If there's no email.html, let's still show inline attachments.
  // NOTE(Kip): Some email clients add inline metadata where there is no inline reference. Until we figure
  // that out lets risk duplicating some attachments ehre.
  // const visibleAttachments = email.html
  //   ? email.attachments.filter((a) => !a.inlineReference)
  //   : email.attachments;
  const visibleAttachments = email.attachments;

  // For delivery failure notifications, use plain text instead of HTML
  const isDeliveryFailure = email.subject === DELIVERY_FAILURE_SUBJECT;

  const sanitizedHtml = isDeliveryFailure
    ? ""
    : sanitizeEmailContent(email.html, email.text);
  return (
    <Box
      sx={{
        overflowX: "auto",
        "& img": {
          maxWidth: "100%",
          maxHeight: "100%",
        },
        "*": {
          textWrap: "wrap",
        },
        blockquote: {
          display: !showOriginal ? "none" : undefined,
        },
        // Note that this might be a Mick specific fix for outlook. lets see!
        "div[id$=ms-outlook-mobile-signature] ~ *": {
          display: !showOriginal ? "none" : undefined,
        },
      }}
    >
      {isDeliveryFailure ? (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
          {email.text}
        </pre>
      ) : (
        <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      )}
      {!showOriginal && (
        <Button
          variant="text"
          sx={(theme) => ({ color: theme.palette.text.secondary })}
          onClick={() => setShowOriginal(true)}
        >
          Show original
        </Button>
      )}
      {visibleAttachments.length > 0 && (
        <Attachments attachments={visibleAttachments} email={email} />
      )}
    </Box>
  );
};
